/**
 * Knowledge Base Detail page — compact document list + upload UI.
 *
 * Each row is a Knowledge Object summary only (no inline expand).
 * Users click "Explore →" to drill into the full KnowledgeObjectDetailPage.
 */
import { useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  DataTable,
  TableContainer,
  TableToolbar,
  TableBatchActions,
  TableBatchAction,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableSelectAll,
  TableSelectRow,
  TableBody,
  TableCell,
  Tag,
  DefinitionTooltip,
  InlineLoading,
  IconButton,
  Modal,
  TextInput,
  TextArea,
  ActionableNotification,
} from '@carbon/react';
import { Add, TrashCan, Download, View, Reset, Renew, Launch, Edit, Information, Layers } from '@carbon/icons-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  useKnowledgeBase,
  useKBDocuments,
  useUploadDocument,
  useDeleteDocument,
  useDocumentMarkdown,
  useProcessLayers,
  useRechunkKnowledgeObject,
  useRetryKnowledgeObject,
  useRenameKnowledgeObject,
  useSuggestDisplayName,
  useUpdateKnowledgeBase,
} from '@/lib/query/hooks';
import { knowledgeBaseApi } from '@/lib/api';
import { outcomeMessage } from '@/lib/query/useResilientMutation';
import { ImportFromKBModal } from '@/components/knowledge-base/ImportFromKBModal';
import { AddDocumentsPanel } from '@/components/knowledge-base/AddDocumentsPanel';
import { DocumentMetadataPanel } from '@/components/knowledge-base/DocumentMetadataPanel';
import { RenameDocumentDialog } from '@/components/knowledge-base/RenameDocumentDialog';
import { useMarkdownComponents } from '@/hooks/useMarkdownComponents';
import type { KBDocument } from '@shared/types/knowledge-base';
import { deriveKOStatus } from '@shared/utils/knowledge-object-status';

// ─── Table column definitions ────────────────────────────────────────────────

const docHeaders = [
  { key: 'filename', header: 'Filename' },
  { key: 'file_size', header: 'Size' },
  { key: 'conversion_status', header: 'Status' },
  { key: 'units', header: 'Knowledge Layers' },
  { key: 'uploaded_at', header: 'Ingested' },
  { key: 'actions', header: '' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


// ─── Page ─────────────────────────────────────────────────────────────────────

export default function KnowledgeBaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: kb, isLoading: kbLoading } = useKnowledgeBase(id || '');
  const { data: documents, isLoading: docsLoading } = useKBDocuments(id || '');
  const uploadMutation = useUploadDocument(id || '');
  const deleteMutation = useDeleteDocument(id || '');
  const rechunkMutation = useRechunkKnowledgeObject(id || '');
  const processLayersMutation = useProcessLayers(id || '');
  const retryMutation = useRetryKnowledgeObject(id || '');
  const renameMutation = useRenameKnowledgeObject(id || '');
  const suggestNameMutation = useSuggestDisplayName();
  const updateKbMutation = useUpdateKnowledgeBase(id || '');

  const [deleteTarget, setDeleteTarget] = useState<KBDocument | null>(null);
  const [renameTarget, setRenameTarget] = useState<KBDocument | null>(null);
  const [previewTarget, setPreviewTarget] = useState<KBDocument | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [metaTarget, setMetaTarget] = useState<KBDocument | null>(null);
  const [editKbOpen, setEditKbOpen] = useState(false);
  /** Progress and per-file outcome of the current (or last failed) multi-file upload. */
  const [uploadQueue, setUploadQueue] = useState<{
    total: number;
    done: number;
    failed: string[];
  } | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  // Bulk selection: ids queued for a group delete (confirmed via modal).
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[]>([]);

  const uploadFailure = outcomeMessage(uploadMutation.error);
  const deleteFailure = outcomeMessage(deleteMutation.error);

  // Fetch markdown content when a document is selected for preview
  const { data: markdownData, isLoading: markdownLoading } = useDocumentMarkdown(
    previewTarget?.id || '',
    !!previewTarget
  );

  // Rewrite image src in the markdown preview modal to use the backend proxy
  const previewMdComponents = useMarkdownComponents(previewTarget?.id || '');

  // Upload queue: SEQUENTIAL, and it does not stop on a failure.
  //
  // The input has always carried `multiple`, but the handler fired every file at once into a
  // single-flight mutation, so each submit overwrote the previous one's state: with 14 files the user
  // saw one outcome and had no idea which of the other 13 had landed. Reported 2026-08-21.
  //
  // Sequential for the same reason bulk delete is (see `handleBulkDelete`): one mutation object holds
  // one outcome. It DIFFERS from bulk delete on failure, deliberately. A delete queue halts, because
  // continuing destroys rows whose state the user can no longer read. An upload queue continues,
  // because one oversized PDF must not cost the other thirteen, and the worst case of continuing is a
  // named list of files to retry. Halting here would have been the safer-looking choice and the worse
  // one.
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files;
      if (!selected || selected.length === 0) return;
      // Snapshot before clearing the input: the FileList is live and empties with `value = ''`.
      const files = Array.from(selected);
      e.target.value = '';

      setUploadQueue({ total: files.length, done: 0, failed: [] });
      const failed: string[] = [];
      for (const [index, file] of files.entries()) {
        // `submit` resolves with null on failure OR on an unconfirmed outcome, and never throws, so
        // the queue cannot be broken by one file.
        const uploaded = await uploadMutation.submit(file);
        if (uploaded === null) failed.push(file.name);
        setUploadQueue({ total: files.length, done: index + 1, failed: [...failed] });
      }
      // Keep the summary when something failed, so the names survive on screen; clear it otherwise.
      if (failed.length === 0) setUploadQueue(null);
    },
    [uploadMutation]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    // `submit` resolves with null on failure instead of throwing; the outcome (including the
    // "we could not confirm" third state) is on the mutation and surfaced below the table.
    const deleted = await deleteMutation.submit(deleteTarget.id);
    if (deleted) setDeleteTarget(null);
  }, [deleteTarget, deleteMutation]);

  // Group actions on the selected rows.
  //
  // Sequential, and it now STOPS on a delete it could not confirm. Before, every id was fired
  // regardless: a link that dropped mid-queue produced a pile of console errors, a bar that
  // closed anyway, and no way to tell which rows had gone. Halting leaves the remaining
  // selection intact so the user can see what is left and press again.
  const handleBulkDelete = useCallback(async () => {
    const remaining = [...bulkDeleteIds];
    while (remaining.length > 0) {
      const docId = remaining[0];
      const deleted = await deleteMutation.submit(docId);
      if (!deleted) break;
      remaining.shift();
    }
    setBulkDeleteIds(remaining);
  }, [bulkDeleteIds, deleteMutation]);

  // Retry (re-ingest) each selected doc: re-fires the COS pipeline, so it works
  // for failed/stuck rows too (rechunk only handles already-completed ones).
  //
  // `clearSelection` is Carbon's own batch-bar `onCancel`, which both hides the
  // bar and deselects the rows. Without it the blue "N items selected" bar stays
  // up after the click, so the only visible outcome of a Refresh is nothing at
  // all: ingestion takes minutes, and the bar covers the toolbar meanwhile, so
  // users conclude the click was swallowed and reload the page. Closing the bar
  // is the acknowledgement that the retries were fired.
  const handleBulkRefresh = useCallback(
    (ids: string[], clearSelection?: () => void) => {
      for (const docId of ids) void retryMutation.submit(docId);
      clearSelection?.();
    },
    [retryMutation]
  );

  const handleDownloadMarkdown = useCallback(
    async (doc: KBDocument, cachedContent?: string) => {
      try {
        let content: string;
        if (cachedContent) {
          content = cachedContent;
        } else {
          const result = await knowledgeBaseApi.getDocumentMarkdown(doc.id);
          content = (result as any).content ?? result;
        }
        const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = doc.filename.replace(/\.[^.]+$/, '.md');
        window.document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 150);
      } catch (err) {
        console.error('Failed to download markdown:', err);
      }
    },
    []
  );

  if (kbLoading) {
    return <InlineLoading description="Loading knowledge base..." />;
  }

  if (!kb) {
    return <p>Knowledge base not found.</p>;
  }

  const rows = (documents || []).map((doc) => {
    const l1 = doc.knowledge_unit_count ?? 0;
    const l2 = doc.summary_count ?? 0;
    const l3 = doc.extraction_count ?? 0;
    const unitsLabel =
      l1 === 0
        ? '—'
        : [`${l1} L1`, l2 > 0 ? `${l2} L2` : null, l3 > 0 ? `${l3} L3` : null]
            .filter(Boolean)
            .join(' · ');
    return {
      id: doc.id,
      filename: doc.display_name || doc.filename,
      file_size: formatFileSize(doc.file_size),
      conversion_status: doc.conversion_status,
      units: unitsLabel,
      uploaded_at: new Date(doc.uploaded_at).toLocaleDateString(),
      actions: doc,
    };
  });

  return (
    <Grid fullWidth style={{ padding: '2rem 0' }}>
      <Column lg={16} md={8} sm={4}>
        {/* Breadcrumb */}
        <Breadcrumb noTrailingSlash style={{ marginBottom: '1rem' }}>
          <BreadcrumbItem onClick={() => navigate('/knowledge-bases')}>
            Knowledge Bases
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>{kb.name}</BreadcrumbItem>
        </Breadcrumb>

        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <h1 style={{ fontSize: '1.75rem', fontWeight: 600 }}>{kb.name}</h1>
              <IconButton
                kind="ghost"
                size="sm"
                label="Edit knowledge base"
                onClick={() => {
                  setEditName(kb.name);
                  setEditDescription(kb.description ?? '');
                  setEditKbOpen(true);
                }}
              >
                <Edit />
              </IconButton>
            </div>
            {kb.description && (
              <p style={{ color: 'var(--cds-text-secondary)', marginTop: '0.25rem' }}>
                {kb.description}
              </p>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.doc,.txt,.md,.pptx,.xlsx"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
        </div>

        {/* Toolbar + upload error — always visible, regardless of row count */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <Button
            kind="primary"
            renderIcon={Add}
            onClick={() => setAddPanelOpen(true)}
          >
            Add Documents
          </Button>
        </div>
        {/* Queue progress and per-file outcome. Shown ABOVE the single-outcome notification below,
            because with several files the question is "which ones", and that notification can only
            ever answer "the last one". */}
        {uploadQueue && (
          uploadQueue.done < uploadQueue.total ? (
            <InlineLoading
              data-testid="upload-progress"
              description={`Uploading ${uploadQueue.done + 1} of ${uploadQueue.total}...`}
              style={{ marginBottom: '1rem' }}
            />
          ) : uploadQueue.failed.length > 0 ? (
            <ActionableNotification
              kind="warning"
              title={`${uploadQueue.failed.length} of ${uploadQueue.total} file(s) did not upload`}
              subtitle={uploadQueue.failed.join(', ')}
              lowContrast
              inline={false}
              actionButtonLabel="Dismiss"
              onActionButtonClick={() => setUploadQueue(null)}
              onCloseButtonClick={() => setUploadQueue(null)}
              style={{ marginBottom: '1rem' }}
              data-testid="upload-queue-failures"
            />
          ) : null
        )}
        {uploadFailure && (
          <ActionableNotification
            kind={uploadMutation.isUnconfirmed ? 'warning' : 'error'}
            title={uploadMutation.isUnconfirmed ? 'Not confirmed' : 'Upload failed'}
            subtitle={uploadFailure}
            lowContrast
            inline={false}
            actionButtonLabel="Retry"
            onActionButtonClick={() => void uploadMutation.retry()}
            onCloseButtonClick={() => uploadMutation.reset()}
            style={{ marginBottom: '1rem' }}
          />
        )}

        {/* Documents table — flat, no expand rows */}
        {docsLoading ? (
          <InlineLoading description="Loading documents..." />
        ) : rows.length === 0 ? (
          <p style={{ color: 'var(--cds-text-secondary)' }}>
            No documents yet. Click &ldquo;Add Documents&rdquo; to get started.
          </p>
        ) : (
          <DataTable rows={rows} headers={docHeaders}>
            {({
              rows: tableRows,
              headers: tableHeaders,
              getTableProps,
              getHeaderProps,
              getRowProps,
              getSelectionProps,
              getBatchActionProps,
              selectedRows,
            }) => (
              <TableContainer>
                <TableToolbar>
                  {(() => {
                    // Read the props once so the batch actions can reuse Carbon's
                    // `onCancel` to dismiss the bar after firing an action.
                    const batchActionProps = getBatchActionProps();
                    return (
                      <TableBatchActions {...batchActionProps}>
                        <TableBatchAction
                          renderIcon={TrashCan}
                          onClick={() => setBulkDeleteIds(selectedRows.map((r) => r.id))}
                        >
                          Delete
                        </TableBatchAction>
                        <TableBatchAction
                          renderIcon={Reset}
                          onClick={() =>
                            handleBulkRefresh(
                              selectedRows.map((r) => r.id),
                              batchActionProps.onCancel
                            )
                          }
                        >
                          Refresh
                        </TableBatchAction>
                      </TableBatchActions>
                    );
                  })()}
                </TableToolbar>
                <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    <TableSelectAll {...getSelectionProps()} />
                    {tableHeaders.map((header) => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.map((row) => {
                    const doc = (documents || []).find((d) => d.id === row.id);
                    if (!doc) return null;

                    const { key: _k, ...rowProps } = getRowProps({ row });
                    void _k;

                    return (
                      <TableRow key={row.id} {...rowProps}>
                        <TableSelectRow {...getSelectionProps({ row })} />
                        {row.cells.map((cell) => {
                          // ── Status tag ──────────────────────────────────
                          if (cell.info.header === 'conversion_status') {
                            const l1 = doc.knowledge_unit_count ?? 0;
                            const l2 = doc.summary_count ?? 0;
                            const l3 = doc.extraction_count ?? 0;
                            const statusInfo = deriveKOStatus(doc.conversion_status, l1, l2, l3, doc.layers_status);
                            const statusTag = (
                              <Tag type={statusInfo.tagType} size="sm">
                                {statusInfo.label}
                              </Tag>
                            );
                            return (
                              <TableCell key={cell.id}>
                                {doc.conversion_status === 'failed' ? (
                                  // Never blind: hovering the red "Failed" tag reveals
                                  // the exact worker error (orphan KB, docling, embed…).
                                  // A failed doc may have no captured message (reset +
                                  // re-fail race); still show a useful tooltip so the
                                  // tag is never a dead end.
                                  <DefinitionTooltip
                                    openOnHover
                                    align="top"
                                    definition={
                                      doc.error_message ||
                                      'Conversion failed and no error detail was captured. Use Refresh / Retry to re-run the pipeline.'
                                    }
                                  >
                                    {statusTag}
                                  </DefinitionTooltip>
                                ) : (
                                  statusTag
                                )}
                              </TableCell>
                            );
                          }

                          // ── Knowledge Layers column ──────────────────────
                          if (cell.info.header === 'units') {
                            const l1 = doc.knowledge_unit_count ?? 0;
                            const l2 = doc.summary_count ?? 0;
                            const l3Units = doc.extraction_unit_count ?? 0;
                            const l3 = doc.extraction_count ?? 0;
                            const l2Eligible = doc.summary_eligible_count ?? 0;
                            const l3Eligible = doc.extraction_eligible_count ?? 0;
                            const isProcessingLayers = doc.layers_status === 'processing';
                            // Show X / Y + bar only while the count is still below the eligible
                            // total. Once the counts are equal the pass is done for that layer.
                            const showL2Progress = isProcessingLayers && l2Eligible > 0 && l2 < l2Eligible;
                            // L3 progress uses units-with-extraction vs eligible units (not raw
                            // extraction row count, which can exceed eligible). Only show after
                            // L2 is done (l2 >= l2Eligible) so the denominator is stable.
                            const l2Done = l2Eligible === 0 || l2 >= l2Eligible;
                            const showL3Progress = isProcessingLayers && l2Done && l3Eligible > 0 && l3Units < l3Eligible;
                            if (l1 === 0) {
                              return (
                                <TableCell key={cell.id}>
                                  <span style={{ color: 'var(--cds-text-secondary)' }}>—</span>
                                </TableCell>
                              );
                            }
                            // Shared mini-bar + fraction badge used by both L2 and L3.
                            const ProgressBadge = ({
                              label, done, total,
                              trackColor, fillColor, borderColor, textColor, titleText,
                            }: {
                              label: string; done: number; total: number;
                              trackColor: string; fillColor: string;
                              borderColor: string; textColor: string; titleText: string;
                            }) => (
                              <span
                                style={{
                                  fontSize: '0.6875rem',
                                  fontVariantNumeric: 'tabular-nums',
                                  padding: '0.1rem 0.3rem',
                                  borderRadius: '2px',
                                  background: trackColor,
                                  border: `1px solid ${borderColor}`,
                                  color: textColor,
                                  display: 'inline-flex',
                                  flexDirection: 'column',
                                  gap: '0.15rem',
                                }}
                                title={titleText}
                              >
                                <span>{label} · {done} / {total}</span>
                                <span style={{ display: 'block', width: '100%', height: '2px', borderRadius: '1px', background: borderColor, overflow: 'hidden' }}>
                                  <span style={{ display: 'block', width: `${Math.min(done / total, 1) * 100}%`, height: '100%', background: fillColor, transition: 'width 0.4s ease' }} />
                                </span>
                              </span>
                            );
                            return (
                              <TableCell key={cell.id}>
                                <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                  {/* L1 — always a plain count */}
                                  <span
                                    style={{
                                      fontSize: '0.6875rem',
                                      fontVariantNumeric: 'tabular-nums',
                                      padding: '0 0.3rem',
                                      borderRadius: '2px',
                                      background: 'var(--cds-layer-02)',
                                      border: '1px solid var(--cds-border-subtle)',
                                      color: 'var(--cds-text-secondary)',
                                    }}
                                    title="L1 — Raw content chunks extracted from the document"
                                  >
                                    L1 · {l1}
                                  </span>
                                  {/* L2 — progress bar while still counting up; plain badge once done */}
                                  {(l2 > 0 || (isProcessingLayers && l2Eligible > 0)) && (
                                    showL2Progress ? (
                                      <ProgressBadge
                                        label="L2" done={l2} total={l2Eligible}
                                        trackColor="rgba(15,98,254,0.06)"
                                        fillColor="rgba(15,98,254,0.8)"
                                        borderColor="rgba(15,98,254,0.25)"
                                        textColor="var(--cds-link-primary, #0f62fe)"
                                        titleText={`L2 — AI-generated summaries: ${l2} of ${l2Eligible} done`}
                                      />
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: '0.6875rem',
                                          fontVariantNumeric: 'tabular-nums',
                                          padding: '0 0.3rem',
                                          borderRadius: '2px',
                                          background: 'rgba(15,98,254,0.06)',
                                          border: '1px solid rgba(15,98,254,0.25)',
                                          color: 'var(--cds-link-primary, #0f62fe)',
                                        }}
                                        title="L2 — AI-generated summaries of each chunk"
                                      >
                                        L2 · {l2}
                                      </span>
                                    )
                                  )}
                                  {/* L3 — progress bar (units done / eligible) once L2 is complete;
                                      pulse dot while L2 is still running; plain badge when complete */}
                                  {(l3 > 0 || isProcessingLayers) && (
                                    showL3Progress ? (
                                      <ProgressBadge
                                        label="L3" done={l3Units} total={l3Eligible}
                                        trackColor="rgba(109,56,210,0.06)"
                                        fillColor="rgba(109,56,210,0.8)"
                                        borderColor="rgba(109,56,210,0.25)"
                                        textColor="#6d38d2"
                                        titleText={`L3 — Structured extractions: ${l3Units} of ${l3Eligible} units done`}
                                      />
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: '0.6875rem',
                                          fontVariantNumeric: 'tabular-nums',
                                          padding: '0 0.3rem',
                                          borderRadius: '2px',
                                          background: 'rgba(109,56,210,0.06)',
                                          border: '1px solid rgba(109,56,210,0.25)',
                                          color: '#6d38d2',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '0.25rem',
                                        }}
                                        title={isProcessingLayers
                                          ? `L3 — Structured extractions in progress (${l3} so far)`
                                          : 'L3 — Structured extractions (entities, captions, tables…)'}
                                      >
                                        L3{l3 > 0 ? ` · ${l3}` : ''}
                                        {isProcessingLayers && (
                                          <span
                                            style={{
                                              width: '5px', height: '5px',
                                              borderRadius: '50%',
                                              background: '#6d38d2',
                                              opacity: 0.7,
                                              animation: 'snxt-pulse 1.2s ease-in-out infinite',
                                            }}
                                          />
                                        )}
                                      </span>
                                    )
                                  )}
                                </div>
                              </TableCell>
                            );
                          }

                          // ── Actions ───────────────────────────────────────
                          if (cell.info.header === 'actions') {
                            return (
                              <TableCell key={cell.id}>
                                <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                  <IconButton
                                    kind="ghost"
                                    size="sm"
                                    label="Document details"
                                    onClick={() => setMetaTarget(doc)}
                                  >
                                    <Information size={16} />
                                  </IconButton>
                                  <IconButton
                                    kind="ghost"
                                    size="sm"
                                    label="Rename document"
                                    onClick={() => setRenameTarget(doc)}
                                    data-testid={`rename-doc-${doc.id}`}
                                  >
                                    <Edit size={16} />
                                  </IconButton>
                                  {/* Explore → drill into KO detail page
                                      Show as soon as L1 units exist (even while L2/L3 are still processing) */}
                                  {(doc.conversion_status === 'completed' || (doc.knowledge_unit_count ?? 0) > 0) && (
                                    <IconButton
                                      kind="ghost"
                                      size="sm"
                                      label="Explore knowledge units"
                                      onClick={() =>
                                        navigate(`/knowledge-bases/${id}/objects/${doc.id}`)
                                      }
                                    >
                                      <Launch size={16} />
                                    </IconButton>
                                  )}

                                  {doc.conversion_status === 'completed' && (
                                    <>
                                      <IconButton
                                        kind="ghost"
                                        size="sm"
                                        label="Preview markdown"
                                        onClick={() => setPreviewTarget(doc)}
                                      >
                                        <View size={16} />
                                      </IconButton>
                                      <IconButton
                                        kind="ghost"
                                        size="sm"
                                        label="Download markdown"
                                        onClick={() => handleDownloadMarkdown(doc)}
                                      >
                                        <Download size={16} />
                                      </IconButton>
                                      {/* Re-run L2/L3 from the LIST, not only from the document page.
                                          Asked for on 2026-08-24, and the reason is the workflow: layer
                                          state is a per-document column shown in THIS table, so the place
                                          you notice a document needs re-running is the place you should be
                                          able to run it. Same intent as the detail page's icon: force,
                                          i.e. redo with the current models. */}
                                      <IconButton
                                        kind="ghost"
                                        size="sm"
                                        label="Re-run L2/L3 layers (summaries + extractions)"
                                        disabled={
                                          processLayersMutation.isPending &&
                                          processLayersMutation.variables === doc.id
                                        }
                                        onClick={() => processLayersMutation.mutate(doc.id)}
                                      >
                                        {processLayersMutation.isPending &&
                                        processLayersMutation.variables === doc.id ? (
                                          <InlineLoading style={{ width: '16px' }} />
                                        ) : (
                                          <Layers size={16} />
                                        )}
                                      </IconButton>
                                      <IconButton
                                        kind="ghost"
                                        size="sm"
                                        label="Re-chunk knowledge units"
                                        disabled={
                                          rechunkMutation.isPending &&
                                          rechunkMutation.variables === doc.id
                                        }
                                        onClick={() => rechunkMutation.mutate(doc.id)}
                                      >
                                        <Reset size={16} />
                                      </IconButton>
                                    </>
                                  )}
                                  {/* Retry (re-ingest) — for any doc not yet completed
                                      (failed / pending / stuck). Re-fires the COS
                                      pipeline; safe to click repeatedly. */}
                                  {doc.conversion_status !== 'completed' && (
                                    <IconButton
                                      kind="ghost"
                                      size="sm"
                                      label={
                                        doc.conversion_status === 'failed'
                                          ? 'Retry processing this failed document'
                                          : 'Retry / re-run ingestion'
                                      }
                                      disabled={
                                        retryMutation.isPending &&
                                        retryMutation.variables?.docId === doc.id
                                      }
                                      onClick={() => void retryMutation.submit(doc.id)}
                                      data-testid={`retry-doc-${doc.id}`}
                                    >
                                      <Renew size={16} />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    kind="ghost"
                                    size="sm"
                                    label="Delete"
                                    onClick={() => setDeleteTarget(doc)}
                                  >
                                    <TrashCan
                                      size={16}
                                      style={{ color: 'var(--cds-support-error)' }}
                                    />
                                  </IconButton>
                                </div>
                              </TableCell>
                            );
                          }

                          // ── Filename — clickable for completed docs ───────
                          if (cell.info.header === 'filename') {
                            const label = String(cell.value);
                            if (doc.conversion_status === 'completed') {
                              return (
                                <TableCell key={cell.id}>
                                  <button
                                    onClick={() => navigate(`/knowledge-bases/${id}/objects/${doc.id}`)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      cursor: 'pointer',
                                      color: 'var(--cds-link-primary, #0f62fe)',
                                      fontFamily: 'inherit',
                                      fontSize: 'inherit',
                                      textAlign: 'left',
                                      textDecoration: 'underline',
                                      textUnderlineOffset: '2px',
                                    }}
                                    title="Open knowledge unit explorer"
                                  >
                                    {label}
                                  </button>
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell key={cell.id}>{label}</TableCell>
                            );
                          }

                          // ── Ingested / uploaded_at ────────────────────────
                          if (cell.info.header === 'uploaded_at') {
                            return (
                              <TableCell key={cell.id}>
                                <span>{String(cell.value)}</span>
                                {doc.conversion_status === 'completed' && doc.converted_at && (
                                  <span
                                    style={{
                                      display: 'block',
                                      color: 'var(--cds-text-secondary)',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Converted: {new Date(doc.converted_at).toLocaleDateString()}
                                  </span>
                                )}
                              </TableCell>
                            );
                          }

                          // ── Default ───────────────────────────────────────
                          return (
                            <TableCell key={cell.id}>{String(cell.value)}</TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TableContainer>
            )}
          </DataTable>
        )}
      </Column>

      {/* Edit KB Modal */}
      <Modal
        open={editKbOpen}
        modalHeading="Edit Knowledge Base"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestClose={() => setEditKbOpen(false)}
        onRequestSubmit={async () => {
          if (!editName.trim()) return;
          await updateKbMutation.mutateAsync({
            name: editName.trim(),
            description: editDescription.trim() || undefined,
          });
          setEditKbOpen(false);
        }}
        primaryButtonDisabled={!editName.trim() || updateKbMutation.isPending}
        size="sm"
      >
        <TextInput
          id="edit-kb-name"
          labelText="Name"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          maxCount={200}
          enableCounter
          style={{ marginBottom: '1rem' }}
        />
        <TextArea
          id="edit-kb-description"
          labelText="Description (optional)"
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={!!deleteTarget}
        danger
        modalHeading="Delete Document"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={handleDelete}
        primaryButtonDisabled={deleteMutation.isPending}
      >
        <p>
          Are you sure you want to delete <strong>{deleteTarget?.filename}</strong>?
        </p>
        {/* Carbon keeps a closed Modal mounted, so this has to be gated on its own modal being
            open or the same notification appears in both confirmations at once. */}
        {deleteFailure && !!deleteTarget && (
          <ActionableNotification
            kind={deleteMutation.isUnconfirmed ? 'warning' : 'error'}
            title={deleteMutation.isUnconfirmed ? 'Not confirmed' : 'Delete failed'}
            subtitle={deleteFailure}
            lowContrast
            hideCloseButton
            inline={false}
            actionButtonLabel="Retry"
            onActionButtonClick={() => void deleteMutation.retry()}
            style={{ marginTop: '1rem' }}
          />
        )}
      </Modal>

      {/* Bulk Delete Confirmation */}
      <Modal
        open={bulkDeleteIds.length > 0}
        danger
        modalHeading="Delete Documents"
        primaryButtonText={`Delete ${bulkDeleteIds.length}`}
        secondaryButtonText="Cancel"
        onRequestClose={() => setBulkDeleteIds([])}
        onRequestSubmit={handleBulkDelete}
        primaryButtonDisabled={deleteMutation.isPending}
      >
        <p>
          Delete <strong>{bulkDeleteIds.length}</strong> selected document
          {bulkDeleteIds.length > 1 ? 's' : ''}? This cannot be undone.
        </p>
        {deleteFailure && bulkDeleteIds.length > 0 && (
          <ActionableNotification
            kind={deleteMutation.isUnconfirmed ? 'warning' : 'error'}
            title={deleteMutation.isUnconfirmed ? 'Not confirmed' : 'Delete failed'}
            subtitle={deleteFailure}
            lowContrast
            hideCloseButton
            inline={false}
            actionButtonLabel="Retry"
            onActionButtonClick={() => void deleteMutation.retry()}
            style={{ marginTop: '1rem' }}
          />
        )}
      </Modal>

      {/* Markdown Preview Modal */}
      <Modal
        open={!!previewTarget}
        modalHeading={previewTarget ? `Preview: ${previewTarget.filename}` : ''}
        primaryButtonText="Download"
        secondaryButtonText="Close"
        onRequestClose={() => setPreviewTarget(null)}
        onRequestSubmit={() => {
          if (previewTarget) handleDownloadMarkdown(previewTarget, markdownData?.content);
        }}
        size="lg"
        passiveModal={false}
      >
        <div
          style={{
            maxHeight: '60vh',
            overflow: 'auto',
            padding: '1rem',
            backgroundColor: 'var(--cds-layer-01)',
            borderRadius: '4px',
          }}
        >
          {markdownLoading ? (
            <InlineLoading description="Loading markdown..." />
          ) : markdownData ? (
            <div className="markdown-preview">
              <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={previewMdComponents}
              >
                {markdownData.content}
              </Markdown>
            </div>
          ) : (
            <p style={{ color: 'var(--cds-text-secondary)' }}>
              No markdown content available.
            </p>
          )}
        </div>
      </Modal>

      <DocumentMetadataPanel
        doc={metaTarget}
        onClose={() => setMetaTarget(null)}
      />

      <AddDocumentsPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        onLocalFile={() => fileInputRef.current?.click()}
        onImportFromKb={() => setImportOpen(true)}
      />

      <ImportFromKBModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        targetKbId={id || ''}
        targetKbName={kb?.name}
      />

      <RenameDocumentDialog
        isOpen={!!renameTarget}
        currentName={renameTarget ? renameTarget.display_name || renameTarget.filename : ''}
        filename={renameTarget?.filename || ''}
        isSaving={renameMutation.isPending}
        canSuggest={
          renameTarget?.conversion_status === 'completed' &&
          (renameTarget?.knowledge_unit_count ?? 0) > 0
        }
        onSuggestName={
          renameTarget
            ? async () => {
                const result = await suggestNameMutation.mutateAsync(renameTarget.id);
                return result.suggested_display_name;
              }
            : undefined
        }
        onSave={async (displayName) => {
          if (!renameTarget) return;
          await renameMutation.mutateAsync({ id: renameTarget.id, displayName: displayName || null });
          setRenameTarget(null);
        }}
        onClose={() => setRenameTarget(null)}
      />
    </Grid>
  );
}

// Made with Bob
